import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";
import { RootState } from "./index";
import liveSessionsApi, { LiveSession } from "./slices/liveSessions";
import { uuidToIntId } from "../../../../helpers/uuid";
import { removeAssessmentNameSuffix } from "../../../../helpers/assessment-name";

export const {
	useGetLiveSessionCountQuery,
	useGetLiveSessionsQuery,
	useGetAssessmentNameSuffixQuery,
	useGetAssessmentQuery,
	useGetLiveSessionActiveStateQuery,
	useEndLiveSessionMutation,
} = liveSessionsApi;

type LiveSessionFilterOptions = { searchText?: string };
type GetLiveSessionsQueryArg = Parameters<typeof useGetLiveSessionsQuery>[0];
type GetLiveSessionsQueryResult = ReturnType<typeof useGetLiveSessionsQuery>;

const filteredLiveSessionsSelector = createSelector(
	(_state: RootState, results: GetLiveSessionsQueryResult) => results,
	(_state: RootState, _results: GetLiveSessionsQueryResult, filterOptions: LiveSessionFilterOptions) => filterOptions,
	(state: RootState) => state,
	(
		results: GetLiveSessionsQueryResult,
		{ searchText }: LiveSessionFilterOptions,
		state: RootState,
	): LiveSession[] | undefined => {
		const lowerSearchText = searchText?.toLowerCase();
		const assessmentNameSuffix = liveSessionsApi.endpoints.getAssessmentNameSuffix.select()(state).data;
		return results.data?.filter(({ assessmentId, name }: LiveSession) => {
			const assessmentName = liveSessionsApi.endpoints.getAssessment.select(uuidToIntId(assessmentId))(state).data
				?.name;
			return (
				!lowerSearchText ||
				name.toLowerCase().includes(lowerSearchText) ||
				(assessmentName &&
					removeAssessmentNameSuffix(assessmentName?.toLowerCase(), assessmentNameSuffix).includes(lowerSearchText))
			);
		});
	},
);

export function useGetFilteredLiveSessions(
	arg: GetLiveSessionsQueryArg,
	filterOptions: LiveSessionFilterOptions,
	options?: SubscriptionOptions,
) {
	const results = useGetLiveSessionsQuery(arg, options);
	return {
		...results,
		data: useSelector((state: RootState) => filteredLiveSessionsSelector(state, results, filterOptions)),
		totalCount: results.data?.length ?? 0,
	};
}
